import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, MenuItem, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import clsx from 'clsx';

import { useGetExerciseOptions } from '../../../../redux/api/exerciseConfigAPI';
import {
  SetType,
  TrainingSetItemStatus,
  actionsWidth,
  setsColumnWidth,
  sideColumnWidth,
  typeColumnWidth,
} from '../../../../shared/constants';
import { IProgramSet } from '../../../../types/interfaces/program-set';
import { IProgramSetItem } from '../../../../types/interfaces/program-set-item';
import { ITrainingSet } from '../../../../types/interfaces/training-set';
import { ITrainingSetItem } from '../../../../types/interfaces/training-set-item';
import { getColumnOptions } from '../../../../utils/ui/column-size-utils';
import BaseActionButton from '../../../designe/ActionButtons/BaseActionButton';
import DeleteActionButton from '../../../designe/ActionButtons/DeleteActionButton';
import AppMenu, {
  PositionOption,
  getPosition,
} from '../../../designe/app-menu/AppMenu';
import AppStandardTextField from '../../../designe/textfields/app-standart-text-field/AppStandardTextField';
import AppTimeSetterField from '../../../designe/wheels/app-time-setter-field/AppTimeSetterField';
import SetTypeIcon from '../../../reusable/set-type-icon/SetTypeIcon';
import TrainingSetItemStatusIcon from '../../../reusable/training-set-item-status-status/TrainingSetItemStatusIcon';
import {
  IUseSetGroupEditTable,
  SetGroupEditTableMode,
  SetGroupFieldName,
} from '../interfaces';
import { useStyles } from './SetItemEdit.styles';
import SetItemOptionEdit from './SetItemOptionEdit';
import { useSetItemEdit } from './useSetItemEdit';

interface ISetItemEditProps {
  setItem: IProgramSetItem | ITrainingSetItem;
  set: IProgramSet | ITrainingSet;
  useSetGroupEditTable: IUseSetGroupEditTable<IProgramSet | ITrainingSet>;
  columnWidth: number;
}

function SetItemEdit(props: ISetItemEditProps) {
  const { setItem, set, useSetGroupEditTable, columnWidth } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const { classes } = useStyles(theme);
  const { onChangeSetItem, onRemoveSetItem, onChangeSetItemOption } =
    useSetItemEdit(useSetGroupEditTable, set, setItem);
  const { data: exerciseOptions } = useGetExerciseOptions();

  const { mode, optionValidators } = useSetGroupEditTable;
  const optionsFieldName = SetGroupFieldName[mode].optionsFieldName;
  const optionFieldName =
    mode === SetGroupEditTableMode.training
      ? useSetGroupEditTable.options.trainingOptionValueField ||
        SetGroupFieldName[mode].optionFieldName
      : SetGroupFieldName[mode].optionFieldName;
  const optionFieldName2 =
    mode === SetGroupEditTableMode.training
      ? useSetGroupEditTable.options.trainingOptionValueField2 ||
        SetGroupFieldName[mode].optionFieldName2
      : SetGroupFieldName[mode].optionFieldName2;
  const setItemsFieldName = SetGroupFieldName[mode].setItemsFieldName;
  // const dndAccept = `programSet_${programSet.setNumber}_${programSet.orderNumber}`;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [statusAnchorEl, setStatusAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  // const ref = useRef(null);

  // const [{ isOver, canDrop }, drop] = useDrop(
  //   {
  //     accept: dndAccept,
  //     drop: ({ orderNumber: oldOrderNumber }) => {
  //       onChangeProgramSetItemOrder(oldOrderNumber, programSetItem.orderNumber);
  //     },
  //     canDrop: ({ orderNumber }: { orderNumber: number }) =>
  //       orderNumber !== programSetItem.orderNumber,
  //     collect: (monitor) => ({
  //       isOver: monitor.isOver(),
  //       canDrop: monitor.canDrop(),
  //     }),
  //   },
  //   [programSetItem],
  // );
  //
  // const [{ isDragging }, drag] = useDrag(() => {
  //   return {
  //     type: dndAccept,
  //     item: () => ({ orderNumber: programSetItem.orderNumber }),
  //     canDrag: false,
  //     collect: (monitor) => ({
  //       isDragging: !!monitor.isDragging(),
  //     }),
  //   };
  // }, [programSetItem]);

  // const getOptionField = useCallback(
  //   (setOption: IProgramSetItemOption, index: number) => {
  //     const option = exerciseOptions?.find((e) => e.id === setOption.optionId);
  //
  //     return (
  //       <Grid item xs={optionSize[orderedOptions?.length]?.option}>
  //         <AppOptionTextField
  //           placeholder={option?.name}
  //           endAdornment={option?.unit}
  //           formik={formik}
  //           name={`${formikFieldName}.programSetItemOptions[${index}].value`}
  //           type={option?.type}
  //         />
  //       </Grid>
  //     );
  //   },
  //   [exerciseOptions, formik],
  // );

  // const isDragTarget = isOver && canDrop;

  // drag(drop(ref));

  const renderSetType = useCallback(() => {
    return (
      <Box {...getColumnOptions(typeColumnWidth)}>
        <AppMenu
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          className={classes.menuContainer}
          anchorOrigin={getPosition(PositionOption.bottom, PositionOption.left)}
          transformOrigin={getPosition(PositionOption.top, PositionOption.left)}
          targetElement={
            <BaseActionButton
              onClick={(e) => {
                setAnchorEl(e.currentTarget as any);
              }}>
              <SetTypeIcon type={setItem.type} />
            </BaseActionButton>
          }>
          {Object.values(SetType).map((type) => (
            <MenuItem
              key={type}
              onClick={() => {
                onChangeSetItem({ ...setItem, type });
                setAnchorEl(null);
              }}
              className={classes.typeItemContainer}>
              <SetTypeIcon type={type} />
              {t(`common.setType_${type}`)}
            </MenuItem>
          ))}
        </AppMenu>
      </Box>
    );
  }, [setItem, anchorEl]);

  const setsError = useMemo(() => {
    if (
      !useSetGroupEditTable.options.hideSetsCount &&
      !(setItem as IProgramSetItem).sets
    )
      return {
        isError: true,
        message: t('formikValidation.setsCountRequired'),
      };

    if ((setItem as IProgramSetItem).sets < 1)
      return {
        isError: true,
        message: t('formikValidation.minValue').replace('{0}', '1'),
      };

    if (!Number.isInteger((setItem as IProgramSetItem).sets))
      return {
        isError: true,
        message: t('formikValidation.integerValueRequired'),
      };

    return { isError: false, message: undefined };
  }, [setItem, useSetGroupEditTable.options.hideSetsCount]);
  const isFirstSetFirstRow =
    set?.setNumber === 0 && set?.orderNumber === 0 && setItem.orderNumber === 0;

  return (
    <Box
      data-onboarding={
        isFirstSetFirstRow ? 'newTraining.firstExerciseRow' : undefined
      }
      display="flex"
      alignItems="center"
      className={clsx(
        classes.setItemWrapper,
        {
          [classes.setItemWrapperWithSplitBySideExercise]:
            set?.exercise?.splitBySide,
          [classes.oddLine]: setItem.orderNumber % 2 === 1,
        },
        // isDragging && classes.itemDragging,
        // isDragTarget && classes.itemDragTarget,
      )}>
      {renderSetType()}

      {!useSetGroupEditTable.options.hideSetsCount && (
        <Box
          data-onboarding={
            isFirstSetFirstRow ? 'newTraining.setsCount' : undefined
          }
          {...getColumnOptions(setsColumnWidth)}
          className={classes.optionFieldContainer}>
          <AppStandardTextField
            placeholder={t('common.sets')}
            selectOnClick={true}
            value={(setItem as IProgramSetItem).sets}
            type="number"
            fullWidth
            onChange={(e) =>
              onChangeSetItem({
                ...setItem,
                sets: e.target.value ? parseInt(e.target.value) : null,
              })
            }
            error={setsError.isError}
            helperText={setsError.message}
          />
        </Box>
      )}

      <Box
        {...getColumnOptions(columnWidth)}
        className={classes.optionFieldContainer}>
        <AppTimeSetterField
          value={setItem.rest}
          onChange={(value) => onChangeSetItem({ ...setItem, rest: value })}
          placeholder={`${t('common.rest')} ${t('common.sec')}`}
        />
      </Box>
      {!!setItem[optionsFieldName].length && (
        <Box display="flex" flexDirection="column" width="100%">
          <Box display="flex" alignItems="center">
            {set?.exercise?.splitBySide && (
              <Box
                display="flex"
                alignItems="center"
                {...getColumnOptions(sideColumnWidth)}>
                <Typography variant={'text-md-sb'}>
                  {t('programs.rightShort')}
                </Typography>
              </Box>
            )}

            {setItem[optionsFieldName].map((setOption, index) => (
              <Box
                key={`${set.id}-${setOption.optionId}`}
                {...getColumnOptions(columnWidth)}>
                <SetItemOptionEdit
                  option={setOption}
                  valueFieldName={optionFieldName}
                  onChange={(value) => onChangeSetItemOption(value)}
                  additionalFieldName={
                    set?.exercise?.splitBySide ? optionFieldName2 : undefined
                  }
                  exerciseOptions={exerciseOptions}
                  optionValidators={optionValidators}
                />
              </Box>
            ))}
          </Box>

          {set?.exercise?.splitBySide && (
            <Box display="flex" alignItems="center">
              <Box {...getColumnOptions(sideColumnWidth)}>
                <Typography variant={'text-md-sb'}>
                  {t('programs.leftShort')}
                </Typography>
              </Box>
              {setItem[optionsFieldName].map((setOption, index) => (
                <Box
                  {...getColumnOptions(columnWidth)}
                  key={`${set.id}-${setOption.optionId}`}>
                  <SetItemOptionEdit
                    option={setOption}
                    optionValidators={optionValidators}
                    onChange={(value) => onChangeSetItemOption(value)}
                    valueFieldName={optionFieldName2}
                    exerciseOptions={exerciseOptions}
                  />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}
      {useSetGroupEditTable.options.showSetItemStatus &&
        mode === SetGroupEditTableMode.training && (
          <Box
            {...getColumnOptions(actionsWidth)}
            display="flex"
            alignItems="center"
            justifyContent="center">
            <AppMenu
              anchorEl={statusAnchorEl}
              onClose={() => setStatusAnchorEl(null)}
              className={classes.menuContainer}
              anchorOrigin={getPosition(
                PositionOption.bottom,
                PositionOption.right,
              )}
              transformOrigin={getPosition(
                PositionOption.top,
                PositionOption.right,
              )}
              targetElement={
                <BaseActionButton
                  onClick={(e) => {
                    setStatusAnchorEl(e.currentTarget as any);
                  }}>
                  <TrainingSetItemStatusIcon
                    status={
                      (setItem as ITrainingSetItem).status ||
                      TrainingSetItemStatus.notStarted
                    }
                  />
                </BaseActionButton>
              }>
              {Object.values(TrainingSetItemStatus)
                .filter((status) => status !== TrainingSetItemStatus.started)
                .map((status) => (
                  <MenuItem
                    key={status}
                    onClick={() => {
                      onChangeSetItem({
                        ...(setItem as ITrainingSetItem),
                        status,
                      });
                      setStatusAnchorEl(null);
                    }}
                    className={classes.typeItemContainer}>
                    <TrainingSetItemStatusIcon status={status} />
                    {t(`trainings.setItemStatus_${status}`)}
                  </MenuItem>
                ))}
            </AppMenu>
          </Box>
        )}
      <Box
        {...getColumnOptions(actionsWidth)}
        display="flex"
        alignItems="center"
        justifyContent="center">
        {set[setItemsFieldName].length > 1 && (
          <DeleteActionButton
            onClick={() => {
              onRemoveSetItem();
            }}
          />
        )}
      </Box>
    </Box>
  );
}

export default SetItemEdit;
